import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import UploadArea from './UploadArea';
import ProcessingStatus from './ProcessingStatus';
import SubtitlePreview from './SubtitlePreview';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--spacing-lg);
  background-color: var(--color-card);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
`;

const Title = styled.h1`
  margin-bottom: var(--spacing-md);
  text-align: center;
`;

const Description = styled.p`
  text-align: center;
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-lg);
  max-width: 600px;
`;

const UserInfo = styled.div`
  width: 100%;
  padding: var(--spacing-md);
  background-color: rgba(0, 113, 227, 0.05);
  border-radius: var(--border-radius-sm);
  margin-bottom: var(--spacing-lg);
  text-align: center;
  color: var(--color-text);
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  color: var(--color-error);
  background-color: rgba(255, 59, 48, 0.1);
  padding: var(--spacing-md);
  border-radius: var(--border-radius-sm);
  margin-bottom: var(--spacing-md);
  width: 100%;
`;

const SubtitleGenerator = ({ user }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [videoId, setVideoId] = useState(null);
  const [subtitleUrl, setSubtitleUrl] = useState(null);
  const [error, setError] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [previewText, setPreviewText] = useState('');

  // Advance processing progress every 12 seconds while generating
  useEffect(() => {
    if (!processing) return;
    const interval = setInterval(() => {
      setProcessingProgress(prev => Math.min(prev + 7, 95));
    }, 12000);
    return () => clearInterval(interval);
  }, [processing]);

  // Poll for the status of the video processing
  const checkProcessingStatus = useCallback(async (id) => {
    try {
      const response = await axios.get(`/api/upload/${id}/`);
      const { status, subtitle_url, transcript_text } = response.data;
      
      if (status === 'completed' && subtitle_url) {
        setProcessing(false);
        setSubtitleUrl(subtitle_url);
        setProcessingProgress(100);
        
        // Use the actual transcript text from the server
        if (transcript_text && transcript_text.trim().length > 0) {
          // Filter out error messages that might have been saved as transcript
          if (transcript_text.includes('No speech detected') || 
              transcript_text.includes('Please check:') ||
              transcript_text.includes('recognition failed')) {
            console.warn('Received error message as transcript, treating as failed');
            setError(transcript_text);
            setProcessing(false);
            return;
          }
          setPreviewText(transcript_text);
        } else {
          // No transcript available - this shouldn't happen if status is 'completed'
          console.warn('No transcript text available for completed video');
          setPreviewText('Transcript generated, but text preview is not available.');
        }
      } else if (status === 'failed') {
        setProcessing(false);
        // Get error message from response if available
        const errorMsg = response.data.error_message || response.data.error || 'Failed to generate transcript. Please try again.';
        setError(errorMsg);
      } else if (status === 'processing') {
        // Continue polling; progress bar advances every 12s via useEffect
        setTimeout(() => checkProcessingStatus(id), 2000);
      }
    } catch (err) {
      setProcessing(false);
      let errorMsg = 'Error checking processing status.';
      
      if (err.response) {
        const data = err.response.data;
        if (data.error_message) {
          errorMsg = data.error_message;
        } else if (data.error) {
          errorMsg = data.error;
        } else if (data.detail) {
          errorMsg = data.detail;
        }
      }
      
      setError(errorMsg);
      console.error('Error checking status:', err);
      console.error('Error response:', err.response?.data);
    }
  }, []);

  // Handle file drop
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    // Reset states
    setError(null);
    setSubtitleUrl(null);
    setPreviewText('');
    
    // Check for rejected files
    if (rejectedFiles && rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors) {
        const errorMessages = rejection.errors.map(err => {
          if (err.code === 'file-too-large') {
            return 'File is too large. Maximum size is 100MB.';
          } else if (err.code === 'file-invalid-type') {
            return 'Invalid file type. Only AVI and MP4 video files are supported.';
          }
          return err.message;
        });
        setError(errorMessages.join(' '));
        return;
      }
    }
    
    const selectedFile = acceptedFiles[0];
    
    // Check if file is an AVI or MP4 video
    const fileExt = selectedFile.name.toLowerCase().split('.').pop();
    if (selectedFile && (fileExt === 'avi' || fileExt === 'mp4')) {
      console.log('File selected:', {
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type,
      });
      setFile(selectedFile);
    } else {
      setError('Please upload an AVI or MP4 video file.');
    }
  }, []);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/x-msvideo': ['.avi'],
      'video/mp4': ['.mp4'],
    },
    maxFiles: 1,
    maxSize: 100 * 1024 * 1024, // 100MB
  });

  // Handle file upload
  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    
    const formData = new FormData();
    formData.append('video_file', file);
    
    try {
      const response = await axios.post('/api/upload/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes timeout for large files
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const pct = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setUploadProgress(pct);
          }
        },
      });
      
      // Start processing
      setUploading(false);
      setProcessing(true);
      setVideoId(response.data.id);
      setProcessingProgress(5); // Start at 5%
      
      // Begin polling for status
      checkProcessingStatus(response.data.id);
      
    } catch (err) {
      setUploading(false);
      
      // Better error handling
      let errorMessage = 'Failed to upload video.';
      
      if (err.response) {
        // Server responded with error status
        const data = err.response.data;
        if (data.detail) {
          // Show detailed error message from server
          errorMessage = data.detail;
        } else if (data.error) {
          errorMessage = data.error;
          // If there's also a detail, append it
          if (data.detail) {
            errorMessage += `: ${data.detail}`;
          }
        } else if (data.video_file) {
          // Validation error from Django
          errorMessage = Array.isArray(data.video_file) 
            ? data.video_file.join(', ') 
            : data.video_file;
        } else if (typeof data === 'string') {
          errorMessage = data;
        } else {
          errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
        }
      } else if (err.request) {
        // Request was made but no response received
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        errorMessage = `Cannot connect to server. Please make sure the backend server is running on ${backendUrl}`;
      } else {
        // Error setting up the request
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
      console.error('Upload error:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        request: err.request,
      });
    }
  };

  // Reset the form
  const handleReset = () => {
    setFile(null);
    setUploading(false);
    setUploadProgress(0);
    setProcessing(false);
    setVideoId(null);
    setSubtitleUrl(null);
    setError(null);
    setProcessingProgress(0);
    setPreviewText('');
  };

  return (
    <Container>
      {user && (
        <UserInfo>
          Logged in as: {user.first_name} {user.last_name} ({user.email})
        </UserInfo>
      )}
      
      <Title>Video to Transcript Converter</Title>
      <Description>
        Upload your AVI or MP4 video and we'll generate a transcript for you. 
        Our advanced speech recognition technology provides accurate transcriptions 
        in text format.
      </Description>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      {!processing && !subtitleUrl && (
        <UploadArea
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          file={file}
          uploading={uploading}
          uploadProgress={uploadProgress}
          handleUpload={handleUpload}
        />
      )}
      
      {processing && (
        <ProcessingStatus 
          status="processing"
          progress={processingProgress} 
          fileName={file?.name}
        />
      )}
      
      {subtitleUrl && (
        <SubtitlePreview
          subtitleUrl={subtitleUrl}
          videoId={videoId}
          previewText={previewText}
          fileName={file?.name}
          handleReset={handleReset}
        />
      )}
    </Container>
  );
};

export default SubtitleGenerator;